import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  CheckCircle, 
  Award, 
  Phone, 
  Calendar,
  Ear,
  Headphones,
  Play,
  Star,
  Shield,
  Heart,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

import VideoCard from "@/components/common/VideoCard";
import TestimonialCard from "@/components/common/TestimonialCard";
import FAQSection from "@/components/common/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stats data
const stats = [
  { number: "37+", label: "Years Experience", icon: Award },
  { number: "10,000+", label: "Surgeries Performed", icon: Shield },
  { number: "1,000+", label: "Cochlear Implants", icon: Ear },
  { number: "4.9", label: "Patient Rating", icon: Star },
];

// Trust credentials
const credentials = [
  "Principal Director - Max Super Speciality Hospital, Saket",
  "International Training (Australia, Germany, USA)",
  "Pioneer in Cochlear Implant Surgery in India",
];

// CI Benefits
const ciBenefits = [
  { text: "Restores functional hearing in severe to profound hearing loss", icon: Ear },
  { text: "Enables speech development in children born deaf", icon: Users },
  { text: "Covered by government schemes (ADIP, State Health) & insurance", icon: Shield },
  { text: "Life-changing outcomes with proper rehabilitation", icon: Heart },
];

// Hearing Aid Benefits
const haBenefits = [
  { text: "Latest digital hearing aid technology", icon: Headphones },
  { text: "Personalized fitting and programming", icon: Users },
  { text: "Solutions for mild to severe hearing loss", icon: Ear },
  { text: "Trusted brands at competitive prices", icon: Shield },
];

export default function HomePage() {
  const [videos, setVideos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [videosRes, testimonialsRes, faqsRes] = await Promise.all([
          axios.get(`${API}/videos?featured_only=true`),
          axios.get(`${API}/testimonials?featured_only=true`),
          axios.get(`${API}/faqs?category=cochlear_implant`),
        ]);
        
        setVideos(videosRes.data);
        setTestimonials(testimonialsRes.data);
        setFaqs(faqsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* Hero Section - Asymmetric Layout */}
      <section className="hero-gradient relative overflow-hidden" data-testid="hero-section">
        <div className="container-width">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[85vh] py-16 lg:py-0">
            {/* Left Content - 7 columns */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 relative z-10"
            >
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full mb-8 shadow-sm">
                <Award className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                <span className="text-sm font-medium text-slate-700">India's Leading Cochlear Implant Surgeon</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display text-[#0A192F] mb-6 leading-[1.1] tracking-tight">
                Restoring Hearing,<br />
                <span className="text-[#1E40AF]">Transforming Lives</span>
              </h1>

              <p className="text-lg lg:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed font-sans">
                Dr. Sanjay Sachdeva is one of India's most experienced Cochlear Implant surgeons with 
                <span className="font-semibold text-[#0A192F]"> 37+ years</span> of expertise in ENT, Head & Neck Surgery.
              </p>

              {/* Credentials */}
              <div className="space-y-3 mb-10">
                {credentials.map((cred, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#1E40AF] flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-slate-600">{cred}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/book-appointment">
                  <Button 
                    size="lg" 
                    className="bg-[#0A192F] hover:bg-[#1E293B] text-white font-semibold shadow-lg h-14 px-8 rounded-full transition-transform hover:scale-105"
                    data-testid="hero-book-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    Book Consultation
                  </Button>
                </Link>
                <a href="tel:+919910317286">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-slate-300 hover:border-[#1E40AF] h-14 px-8 rounded-full transition-colors"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    9910317286
                  </Button>
                </a>
              </div>

              {/* Operating hours */}
              <div className="text-sm text-slate-500">
                <span className="font-medium">Clinic Hours:</span> Mon-Sat: 10:00 AM - 7:00 PM
              </div>
            </motion.div>

            {/* Right - Doctor Photo - 5 columns */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                {/* Background shape */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1E40AF]/10 to-transparent rounded-3xl transform rotate-3" />
                
                {/* Doctor's Photo */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200/50">
                  <img
                    src="https://drssachdeva.com/wp-content/uploads/2024/12/20180510_184533-1024x576.jpg"
                    alt="Dr. Sanjay Sachdeva - ENT & Cochlear Implant Surgeon"
                    className="w-full aspect-[4/3] object-cover object-top"
                  />
                </div>
                
                {/* Floating Info Card */}
                <motion.div 
                  className="absolute -bottom-6 -left-6 right-6 bg-white rounded-xl p-6 shadow-xl border border-slate-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#0A192F] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg font-display">SS</span>
                    </div>
                    <div>
                      <div className="font-display font-semibold text-[#0A192F] text-lg">Dr. Sanjay Sachdeva</div>
                      <div className="text-[#1E40AF] font-medium text-sm">Principal Director - ENT & Cochlear Implant</div>
                      <div className="text-slate-500 text-sm mt-0.5">MBBS, MS (ENT), FICS</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Dark Navy */}
      <section className="medical-trust-section py-16 lg:py-20" data-testid="stats-section">
        <div className="container-width relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-[#1E40AF] mx-auto mb-4" strokeWidth={1.5} />
                <div className="text-4xl lg:text-5xl font-display font-semibold text-white mb-2">{stat.number}</div>
                <div className="text-sm font-medium text-slate-400 uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Services - Bento Grid */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="services-section">
        <div className="container-width">
          <div className="text-center mb-16">
            <span className="badge-category mb-4">Our Specializations</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display text-[#0A192F] mb-4">
              Expert Hearing Solutions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive care for all types of hearing loss, from advanced surgical interventions to premium hearing devices.
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Cochlear Implant Card - Featured */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-[#1E40AF]" />
              <div className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#1E40AF]/10 flex items-center justify-center">
                    <Ear className="w-6 h-6 text-[#1E40AF]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-semibold text-[#0A192F]">Cochlear Implants</h3>
                    <span className="text-sm text-[#1E40AF] font-medium">For Severe to Profound Hearing Loss</span>
                  </div>
                </div>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  When hearing aids are no longer enough, cochlear implants offer a life-changing solution. 
                  Dr. Sachdeva has performed over <span className="font-semibold text-[#0A192F]">1,000+ cochlear implant surgeries</span> with exceptional outcomes.
                </p>

                <div className="space-y-3 mb-8">
                  {ciBenefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <benefit.icon className="w-5 h-5 text-[#1E40AF] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-slate-600 text-sm">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link to="/cochlear-implant">
                    <Button className="bg-[#0A192F] hover:bg-[#1E293B] rounded-full">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                  <Link to="/cochlear-implant/products">
                    <Button variant="outline" className="border-slate-300 rounded-full">
                      View CI Products
                    </Button>
                  </Link>
                </div>
              </div>

              {/* CI Image */}
              <div className="relative h-48 lg:h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1713403857782-80a39b5af884?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxjb2NobGVhciUyMGltcGxhbnQlMjBkZXZpY2UlMjBjbG9zZSUyMHVwJTIwbWVkaWNhbCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcyMTg2NTU0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Cochlear Implant Device"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            {/* Hearing Aids Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-emerald-600" />
              <div className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-semibold text-[#0A192F]">Hearing Aids</h3>
                    <span className="text-sm text-emerald-600 font-medium">For Mild to Severe Hearing Loss</span>
                  </div>
                </div>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Premium digital hearing aids from world-renowned brands. Expert fitting, programming, and ongoing support 
                  through our partner <span className="font-semibold text-[#0A192F]">HearClear</span>.
                </p>

                <div className="space-y-3 mb-8">
                  {haBenefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <benefit.icon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-slate-600 text-sm">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <Link to="/hearing-aids">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
                    Explore Hearing Aids
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>

              {/* Hearing Aid Image */}
              <div className="relative h-48 lg:h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1692160756327-f0481e950b28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBoZWFyaW5nJTIwYWlkJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzQyNzM1NzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Hearing Aid"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>
          </div>

          {/* CSR Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 border border-rose-200"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-rose-500 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-xl font-display font-semibold text-[#0A192F]">Support Deaf Children</h3>
                  <p className="text-slate-600">Help fund cochlear implant surgeries for underprivileged children</p>
                </div>
              </div>
              <Link to="/csr">
                <Button className="bg-rose-600 hover:bg-rose-700 rounded-full whitespace-nowrap">
                  Learn How to Help
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="section-padding bg-white" data-testid="videos-section">
        <div className="container-width">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="badge-category mb-4">Educational Resources</span>
              <h2 className="text-3xl md:text-4xl font-display text-[#0A192F]">
                Patient Education Videos
              </h2>
              <p className="text-lg text-slate-600 mt-4 max-w-xl">
                Learn about cochlear implants, hearing health, and ENT procedures from Dr. Sachdeva's expert guidance.
              </p>
            </div>
            <Link to="/videos">
              <Button variant="outline" className="rounded-full border-slate-300">
                <Play className="w-4 h-4 mr-2" strokeWidth={1.5} />
                View All Videos
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-video bg-slate-200 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.slice(0, 3).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-[#F8FAFC]" data-testid="testimonials-section">
          <div className="container-width">
            <div className="text-center mb-12">
              <span className="badge-category mb-4">Patient Stories</span>
              <h2 className="text-3xl md:text-4xl font-display text-[#0A192F] mb-4">
                What Our Patients Say
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Real stories from patients who have experienced life-changing hearing restoration.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Preview */}
      {faqs.length > 0 && (
        <FAQSection faqs={faqs} title="Frequently Asked Questions" />
      )}

      {/* CTA Section */}
      <section className="medical-trust-section py-20 lg:py-28" data-testid="cta-section">
        <div className="container-width relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-semibold text-white mb-6">
              Ready to Restore Your Hearing?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
              Take the first step towards better hearing. Book a consultation with Dr. Sanjay Sachdeva today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book-appointment">
                <Button 
                  size="lg" 
                  className="bg-white text-[#0A192F] hover:bg-slate-100 font-semibold h-14 px-8 rounded-full shadow-lg transition-transform hover:scale-105"
                  data-testid="cta-book-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Book Appointment
                </Button>
              </Link>
              <a href="tel:+919910317286">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 h-14 px-8 rounded-full"
                >
                  <Phone className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Call: 9910317286
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
